import ProductEntity from '@/entities/api/products/products'
import { Box, Flex, Heading, Text, Badge, Grid, Image } from '@chakra-ui/react'
import { API_URL } from '@/services/api/keys'
import entityConvert from '@/utils/entity-convert'
import Icon from '../shared/icons'
import nameAttributeConvert from '@/utils/name-attributes-convert'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/store/store'
import { ToastConfirm } from '../elements/toast-confirm'
import Toast from '../elements/toast'
import { sessionRefrestUpdate } from '@/store/features/state'
import { deleteProductFn } from '@/store/features/products'
import { removeProductById } from '@/services/api/products/products'
import { useState } from 'react'

const StockProductCard = ({
	click,
	product,
	token,
}: {
	click: () => void
	product: ProductEntity
	token: string
}) => {
	const dispatch = useDispatch<AppDispatch>()
	const [moreDetails, setMoreDetails] = useState<boolean>(false)

	const totalPrice =
		product?.attributes?.reduce((sum, attr) => sum + (attr.price || 0), 0) || 0
	const totalStock =
		product?.attributes?.reduce((sum, attr) => sum + (attr.quantity || 0), 0) ||
		0

	const removeProductFn = async (product: ProductEntity) => {
		try {
			ToastConfirm({
				message: '¿Quieres eliminar este elemento?',
				onConfirm: async () => {
					const response = await removeProductById(token, product?._id ?? '')

					if ([404, 500].includes(response?.status || 0)) {
						Toast({
							type: 'error',
							message: 'No se pudo eliminar el producto',
						})
						return
					}

					if (response?.status === 401) {
						Toast({
							type: 'info',
							message: 'La session del usuario a expirado',
						})
						dispatch(sessionRefrestUpdate())
						return
					}

					dispatch(deleteProductFn(product._id ?? ''))
					Toast({
						type: 'success',
						message: 'Se elimino correctamente el producto',
					})
				},
				onCancel: () => {
					Toast({
						type: 'info',
						message: 'Se cancelo la operacion',
					})
				},
				confirmLabel: 'Eliminar',
				cancelLabel: 'Cancelar',
			})
		} catch (error) {
			Toast({ type: 'error', message: 'No se pudo eliminar el producto' })
		}
	}

	const renderAttributeDetails = (attr: any) => {
		const names = Array.isArray(attr.name) ? attr.name : [attr.name]
		const values = Array.isArray(attr.value) ? attr.value : [attr.value]
		const references = Array.isArray(attr.reference)
			? attr.reference
			: [attr.reference]

		const attributeItems = []
		const maxLength = Math.max(names.length, values.length, references.length)

		for (let i = 0; i < maxLength; i++) {
			if (names[i] && values[i]) {
				attributeItems.push({
					name: names[i],
					value: values[i],
					reference: references[i] || 'Sin ref',
				})
			}
		}

		if (attributeItems.length === 1) {
			return `${nameAttributeConvert(attributeItems[0].name)}: ${
				attributeItems[0].value
			}`
		} else if (attributeItems.length > 1) {
			const types = [
				...new Set(
					attributeItems.map((item) => nameAttributeConvert(item.name))
				),
			]
			return types.join(' + ')
		}

		return 'Atributo sin datos'
	}

	return (
		<Box
			bg='white'
			overflow='hidden'
			boxShadow='md'
			borderRadius='xl'
			position='relative'
			border='1px solid'
			borderColor='gray.100'
			transition='all 0.2s'
			_hover={{
				boxShadow: 'lg',
				transform: 'translateY(-2px)',
				borderColor: 'gray.200',
			}}
		>
			<Flex p={{ base: '3', md: '4' }} direction='column' h='full' gap='3'>
				<Flex w='full' justify='space-between' align='center'>
					<Text
						fontSize={{ base: 'xs', md: 'sm' }}
						fontWeight='500'
						color='gray.500'
						fontFamily='mono'
					>
						#{product?._id?.slice(-8) ?? '--------'}
					</Text>

					<Flex gap='2' zIndex={2}>
						<Box
							cursor='pointer'
							p='1'
							borderRadius='md'
							_hover={{ bg: 'red.50' }}
							transition='background 0.2s'
						>
							<Icon
								icon='delete'
								size='lg'
								color='red.500'
								click={() => removeProductFn(product)}
							/>
						</Box>
						<Box
							cursor='pointer'
							p='1'
							borderRadius='md'
							_hover={{ bg: 'gray.100' }}
							transition='background 0.2s'
						>
							<Icon
								icon='ellipsis'
								size='lg'
								click={() => setMoreDetails((prev) => !prev)}
								color='gray.600'
							/>
						</Box>
					</Flex>
				</Flex>

				<Box
					onClick={click}
					cursor='pointer'
					flex='1'
					display='flex'
					flexDirection='column'
				>
					{!moreDetails ? (
						<Flex direction='column' h='full' gap='3'>
							{product?.images && product?.images?.length > 0 && (
								<Box
									position='relative'
									w='full'
									h='240px'
									borderRadius='lg'
									overflow='hidden'
									bg='gray.50'
									border='1px solid'
									borderColor='gray.100'
								>
									<Image
										src={`${API_URL}/${product.images[0].url}`}
										alt={product.name || 'Producto'}
										w='full'
										h='full'
										objectFit='cover'
									/>
								</Box>
							)}

							<Flex direction='column' gap='2' flex='1'>
								<Flex gap='1' flexWrap='wrap'>
									<Badge
										colorScheme='blue'
										fontSize='xs'
										px='2'
										py='1'
										borderRadius='full'
									>
										{entityConvert(product?.type)}
									</Badge>
									<Badge
										colorScheme='green'
										fontSize='xs'
										px='2'
										py='1'
										borderRadius='full'
									>
										{product?.category?.name || 'Sin categoría'}
									</Badge>
									<Badge
										colorScheme={product?.active ? 'green' : 'red'}
										fontSize='xs'
										px='2'
										py='1'
										borderRadius='full'
									>
										{product?.active ? 'Activo' : 'Inactivo'}
									</Badge>
								</Flex>

								<Heading
									as='h3'
									fontSize={{ base: 'md', md: 'lg' }}
									color='gray.800'
									fontWeight='600'
									overflow='hidden'
									textOverflow='ellipsis'
									whiteSpace='nowrap'
									title={product?.name || 'Sin nombre'}
								>
									{product?.name || 'Sin nombre'}
								</Heading>

								<Flex justify='space-between' align='center' mt='auto'>
									<Text fontSize='sm' color='gray.600' fontWeight='500'>
										${totalPrice.toLocaleString()}
									</Text>
									<Text fontSize='sm' color='gray.600'>
										{totalStock} uds
									</Text>
								</Flex>
							</Flex>
						</Flex>
					) : (
						<Flex direction='column' h='380px' gap='3'>
							<Box h='1px' bg='gray.200' w='full' />

							<Flex justify='space-between' align='center'>
								<Text fontSize='sm' fontWeight='600' color='gray.700'>
									Atributos del Producto
								</Text>
								<Text
									fontSize='sm'
									fontWeight='500'
									color='gray.500'
									overflow='hidden'
									textOverflow='ellipsis'
									whiteSpace='nowrap'
									maxW='120px'
								>
									{product?.name || 'Sin nombre'}
								</Text>
							</Flex>

							<Box
								flex='1'
								overflowY='auto'
								css={{
									'&::-webkit-scrollbar': {
										width: '4px',
									},
									'&::-webkit-scrollbar-track': {
										background: 'transparent',
									},
									'&::-webkit-scrollbar-thumb': {
										background: '#CBD5E0',
										borderRadius: '2px',
									},
									'&::-webkit-scrollbar-thumb:hover': {
										background: '#A0AEC0',
									},
								}}
							>
								<Flex direction='column' gap='2'>
									{product?.attributes?.map((attr) => (
										<Box
											key={attr._id}
											bg='gray.50'
											borderRadius='lg'
											p='3'
											border='1px solid'
											borderColor='gray.100'
										>
											<Grid
												templateColumns='50px 1fr'
												gap='3'
												alignItems='center'
											>
												<Box
													borderRadius='md'
													overflow='hidden'
													bg='white'
													border='1px solid'
													borderColor='gray.200'
												>
													<Image
														src={`${API_URL}/${attr.image?.url || ''}`}
														alt='Atributo'
														w='50px'
														h='50px'
														objectFit='cover'
													/>
												</Box>

												<Flex direction='column' gap='1' minW={0}>
													<Text
														fontSize='sm'
														fontWeight='600'
														color='gray.800'
														overflow='hidden'
														textOverflow='ellipsis'
														whiteSpace='nowrap'
													>
														{renderAttributeDetails(attr)}
													</Text>

													<Flex justify='space-between' align='center'>
														<Text
															fontSize='xs'
															color='gray.600'
															fontWeight='500'
														>
															${attr.price?.toLocaleString()}
														</Text>
														<Text
															fontSize='xs'
															color='gray.600'
															fontWeight='500'
														>
															{attr.quantity} uds
														</Text>
													</Flex>
												</Flex>
											</Grid>
										</Box>
									))}
								</Flex>
							</Box>

							<Box
								bg='gray.50'
								p='3'
								borderRadius='lg'
								border='1px solid'
								borderColor='gray.200'
							>
								<Text fontSize='sm' fontWeight='600' color='gray.700' mb='2'>
									Resumen Total
								</Text>

								<Box h='1px' bg='gray.200' w='full' mb='2' />

								<Flex justify='space-between' align='center'>
									<Text fontSize='sm' fontWeight='600' color='gray.800'>
										${totalPrice.toLocaleString()}
									</Text>
									<Text fontSize='sm' fontWeight='600' color='gray.800'>
										{totalStock} unidades
									</Text>
								</Flex>
							</Box>
						</Flex>
					)}
				</Box>
			</Flex>
		</Box>
	)
}

export default StockProductCard
